// src/components/Console/Staff/pages/ReceptionCheckInOut.jsx
import React, { useEffect, useMemo, useState, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './ReceptionCheckInOut.css';
import { StateContext } from '../../../../../../StateProvider';
import {
  fetchBookings,
  updateBookingNote,
  checkInBooking,
  checkOutBooking,
  resendReceiptForBooking
} from '../../../../../../Utilities/api';
import { BackButton } from '../../../../../../Utilities/components/Buttons';

function safeString(v) { return v == null ? '' : String(v); }
function lower(v) { return safeString(v).toLowerCase(); }
function includesText(h, n) {
  const H = lower(h);
  const N = lower(n);
  if (!N) return true;
  return H.indexOf(N) !== -1;
}
function toISODateOnly(d) {
  try {
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    const day = d.getDate();
    const mm = m < 10 ? '0' + m : String(m);
    const dd = day < 10 ? '0' + day : String(day);
    return y + '-' + mm + '-' + dd;
  } catch (_) { return ''; }
}

const ITEMS_PER_PAGE = 10;

export default function ReceptionCheckInOut() {
  const { user } = useContext(StateContext) || {};
  const navigate = useNavigate();
  const searchInputRef = useRef(null);

  // data
  const [allBookings, setAllBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // filters
  const [dayFilter, setDayFilter] = useState('today'); // today | upcoming | past
  const [serviceFilter, setServiceFilter] = useState('all'); // daycare | grooming | hotel | training | all
  const [statusFilter, setStatusFilter] = useState('all'); // booked | checked_in | checked_out | canceled | all
  const [search, setSearch] = useState('');

  // actions
  const [noteDraft, setNoteDraft] = useState('');
  const [activeBookingId, setActiveBookingId] = useState(null);

  // pagination
  const [currentPage, setCurrentPage] = useState(1);

  const todayISO = useMemo(() => toISODateOnly(new Date()), []);

  // initial fetch
  useEffect(() => {
    reload(true);
    // focus search on mount (helps front desk flow)
    if (searchInputRef.current) {
      try { searchInputRef.current.focus(); } catch (_) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function reload(showSpinner) {
    if (showSpinner) setLoading(true);
    setError('');
    try {
      const res = await fetchBookings();
      setAllBookings(Array.isArray(res) ? res : []);
    } catch (e) {
      setError((e && e.message) || 'Failed to load bookings');
      setAllBookings([]);
    } finally {
      if (showSpinner) setLoading(false);
    }
  }

  // helpers
  function bookingDateISO(b) {
    const raw = (b && b.startTime) ? b.startTime : (b && b.date ? b.date : null);
    if (!raw) return '';
    try { return toISODateOnly(new Date(raw)); } catch (_) { return ''; }
  }
  function isToday(b) { return bookingDateISO(b) === todayISO; }
  function isPast(b) { return bookingDateISO(b) < todayISO; }
  function isUpcoming(b) { return bookingDateISO(b) > todayISO; }

  function dayMatches(b, which) {
    if (which === 'today') return isToday(b);
    if (which === 'upcoming') return isUpcoming(b);
    if (which === 'past') return isPast(b);
    return true;
  }
  function serviceMatches(b, wanted) {
    if (wanted === 'all') return true;
    const s = b && b.bookingType ? lower(b.bookingType) : '';
    return s === lower(wanted);
  }
  function statusMatches(b, wanted) {
    if (wanted === 'all') return true;
    const s = b && b.status ? lower(b.status) : '';
    return s === lower(wanted);
  }
  function searchMatches(b, q) {
    if (!q || q.trim() === '') return true;

    // pets
    let petOk = false;
    const pets = b && Array.isArray(b.selectedPets) ? b.selectedPets : [];
    for (let i = 0; i < pets.length; i++) {
      const p = pets[i];
      const name = p && typeof p === 'object' && p.petName ? p.petName : safeString(p);
      if (includesText(name, q)) { petOk = true; break; }
    }

    const owner = b && b.ownerName ? b.ownerName : (b && b.user && b.user.name ? b.user.name : '');
    const idStr = safeString(b && b.id);
    const svc = b && b.bookingType ? b.bookingType : '';
    const ref = b && b.reference ? b.reference : '';

    return petOk || includesText(owner, q) || includesText(idStr, q) || includesText(svc, q) || includesText(ref, q);
  }

  // filter + paginate
  function filterBookings(list) {
    const out = [];
    for (let i = 0; i < list.length; i++) {
      const b = list[i];
      if (!dayMatches(b, dayFilter)) continue;
      if (!serviceMatches(b, serviceFilter)) continue;
      if (!statusMatches(b, statusFilter)) continue;
      if (!searchMatches(b, search)) continue;
      out.push(b);
    }
    return out;
  }

  const filtered = filterBookings(allBookings);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const pageItems = filtered.slice(startIndex, endIndex);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  // actions (optimistic)
  async function onCheckIn(b) {
    // optimistic
    const prev = allBookings.slice();
    const next = prev.map(x => x.id === b.id ? { ...x, status: 'checked_in' } : x);
    setAllBookings(next);
    try {
      await checkInBooking(b.id, { at: new Date().toISOString() });
    } catch (e) {
      alert((e && e.message) || 'Check-in failed');
      setAllBookings(prev); // revert
    }
  }
  async function onCheckOut(b) {
    const prev = allBookings.slice();
    const next = prev.map(x => x.id === b.id ? { ...x, status: 'checked_out' } : x);
    setAllBookings(next);
    try {
      await checkOutBooking(b.id, { at: new Date().toISOString() });
    } catch (e) {
      alert((e && e.message) || 'Check-out failed');
      setAllBookings(prev);
    }
  }
  async function onSaveNote() {
    if (!activeBookingId) return;
    try {
      await updateBookingNote(activeBookingId, noteDraft);
      const updated = allBookings.map(b => b.id === activeBookingId ? { ...b, specialNotes: noteDraft } : b);
      setAllBookings(updated);
      setNoteDraft('');
      setActiveBookingId(null);
    } catch (e) {
      alert((e && e.message) || 'Saving note failed');
    }
  }
  async function onResendReceipt(b) {
    try {
      await resendReceiptForBooking(b.id);
      alert('Receipt sent.');
    } catch (e) {
      alert((e && e.message) || 'Failed to resend receipt');
    }
  }

  function openBooking(b) {
    // go to booking details for quick edits
    navigate('/console/booking/' + b.id, { state: { booking: b } });
  }

  function formatPets(b) {
    const pets = b && Array.isArray(b.selectedPets) ? b.selectedPets : [];
    const names = [];
    for (let i = 0; i < pets.length; i++) {
      const p = pets[i];
      const nm = p && typeof p === 'object' && p.petName ? p.petName : safeString(p);
      if (nm) names.push(nm);
    }
    return names.length ? names.join(', ') : '—';
  }

  function onSearchKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      setCurrentPage(1);
      // live filtering already applied; this just prevents accidental form submit
    }
  }

  // render
  if (loading) return <div className="rco-loading">Loading check-ins…</div>;
  if (error) return <div className="rco-error">Error: {error}</div>;

  return (
    <div className="rco-wrap">
        <BackButton />
      <div className="rco-topbar">
        <h2 className="rco-title">🐾 Reception · Check In / Out</h2>

        <div className="rco-filters" role="search">
          <label className="rco-filter">
            <span>Day</span>
            <select
              className="rco-select"
              value={dayFilter}
              onChange={e => { setDayFilter(e.target.value); setCurrentPage(1); }}
            >
              <option value="today">Today</option>
              <option value="upcoming">Upcoming</option>
              <option value="past">Past</option>
            </select>
          </label>

          <label className="rco-filter">
            <span>Service</span>
            <select
              className="rco-select"
              value={serviceFilter}
              onChange={e => { setServiceFilter(e.target.value); setCurrentPage(1); }}
            >
              <option value="all">All</option>
              <option value="daycare">Daycare</option>
              <option value="grooming">Grooming</option>
              <option value="hotel">Hotel</option>
              <option value="training">Training</option>
            </select>
          </label>

          <label className="rco-filter">
            <span>Status</span>
            <select
              className="rco-select"
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            >
              <option value="all">All</option>
              <option value="booked">Booked</option>
              <option value="checked_in">Checked In</option>
              <option value="checked_out">Checked Out</option>
              <option value="canceled">Canceled</option>
            </select>
          </label>

          <div className="rco-search">
            <input
              ref={searchInputRef}
              className="rco-input"
              placeholder="Search pet, owner, id, ref"
              value={search}
              onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
              onKeyDown={onSearchKeyDown}
              aria-label="Search bookings"
            />
          </div>
        </div>
      </div>

      {pageItems.length === 0 ? (
        <div className="rco-empty">No bookings match your filters.</div>
      ) : (
        <div className="rco-list">
          {pageItems.map(b => {
            const when = b && b.startTime
              ? new Date(b.startTime).toLocaleString()
              : (bookingDateISO(b) || '—');

            const petNames = formatPets(b);
            const status = b && b.status ? b.status : 'booked';
            const canCheckIn = status === 'booked';
            const canCheckOut = status === 'checked_in';

            return (
              <div key={b.id} className="rco-row">
                <button type="button" className="rco-main" onClick={() => openBooking(b)}>
                  <div className="rco-line1">
                    <span className="rco-id">#{safeString(b && b.id)}</span>
                    <span className={'rco-badge ' + lower(status)}>{lower(status)}</span>
                    <span className="rco-type">{safeString(b && b.bookingType)}</span>
                    <span className="rco-when">{when}</span>
                  </div>
                  <div className="rco-line2">
                    <span className="rco-pets">Pets: {petNames}</span>
                    <span className="rco-owner">Owner: {safeString(b && b.ownerName)}</span>
                    <span className="rco-ref">Ref: {safeString(b && b.reference)}</span>
                    {b && b.specialNotes ? (
                      <span className="rco-notes" title={b.specialNotes}>📝 {b.specialNotes}</span>
                    ) : null}
                  </div>
                </button>

                <div className="rco-actions">
                  {canCheckIn ? (
                    <button className="rco-btn rco-btn--primary" onClick={() => onCheckIn(b)}>Check In</button>
                  ) : null}
                  {canCheckOut ? (
                    <button className="rco-btn rco-btn--warn" onClick={() => onCheckOut(b)}>Check Out</button>
                  ) : null}
                  <button className="rco-btn rco-btn--ghost" onClick={() => onResendReceipt(b)}>Receipt</button>
                  <button
                    className="rco-btn"
                    onClick={() => { setActiveBookingId(b.id); setNoteDraft(safeString(b && b.specialNotes)); }}
                  >
                    Note
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="rco-pagination">
        <button
          className="rco-btn"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
        >
          Previous
        </button>
        <span className="rco-pageinfo">Page {currentPage} of {totalPages}</span>
        <button
          className="rco-btn"
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(currentPage + 1)}
        >
          Next
        </button>
      </div>

      {activeBookingId ? (
        <div className="rco-note-modal" role="dialog" aria-modal="true">
          <div className="rco-note-card">
            <div className="rco-note-title">Update Note · #{activeBookingId}</div>
            <textarea
              className="rco-note-text"
              value={noteDraft}
              onChange={e => setNoteDraft(e.target.value)}
            />
            <div className="rco-note-actions">
              <button className="rco-btn rco-btn--primary" onClick={onSaveNote}>Save</button>
              <button className="rco-btn rco-btn--ghost" onClick={() => setActiveBookingId(null)}>Cancel</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
